/**
 * EditMCPServerModal - Edit an existing MCP server configuration
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import type { MCPServerConfigFrontend, MCPTransportType, Language } from '@/types/integrations'
import { isValidHttpUrl } from '@/lib/integrations/integrationUtils'
import { useMcpDiscovery } from '@/hooks/api/useIntegrations'

interface EditMCPServerModalProps {
  open: boolean
  onClose: () => void
  onSave: (entry: MCPServerConfigFrontend, index: number) => Promise<boolean>
  server: MCPServerConfigFrontend | null
  index: number
  language: Language
}

export function EditMCPServerModal({
  open,
  onClose,
  onSave,
  server,
  index,
  language,
}: EditMCPServerModalProps) {
  const { t } = useTranslation()
  const [alias, setAlias] = useState('')
  const [url, setUrl] = useState('')
  const [type, setType] = useState<MCPTransportType>('streamable_http')
  const [authToken, setAuthToken] = useState('')
  const [headers, setHeaders] = useState<Array<{ key: string; value: string }>>([])
  const [dynamicConfig, setDynamicConfig] = useState<any>({})
  const [isSaving, setIsSaving] = useState(false)

  const { data } = useMcpDiscovery(server?.url)
  const serverInfo = data?.server_info

  // Load server data when modal opens
  useEffect(() => {
    if (server) {
      setAlias(server.alias || '')
      setUrl(server.url || '')
      setType(server.type || 'streamable_http')
      setAuthToken(server.auth_token || '')
      setHeaders(
        Object.entries(server.headers || {}).map(([key, value]) => ({ key, value }))
      )
      setDynamicConfig(server.config || {})
    }
  }, [server])

  if (!open || !server) return null

  const displayTitle =
    serverInfo?.name || server.alias || (server.url ? new URL(server.url, window.location.origin).hostname : t('resources.assistants.integrations.mcp_server'))

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const headersRecord: Record<string, string> = {}
      headers.forEach(({ key, value }) => {
        if (key) headersRecord[key] = value
      })

      const entry: MCPServerConfigFrontend = {
        alias: alias || undefined,
        url,
        type,
        auth_token: authToken || undefined,
        headers: Object.keys(headersRecord).length ? headersRecord : undefined,
        config: dynamicConfig || {},
      }

      const success = await onSave(entry, index)
      if (success) {
        onClose()
      }
    } finally {
      setIsSaving(false)
    }
  }

  const isValid = url && isValidHttpUrl(url)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <img
              src={serverInfo?.icon || '/images/mcp.svg'}
              alt="MCP"
              className="h-8 w-8 rounded-md"
            />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('resources.assistants.integrations.edit_title', { name: displayTitle })}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {/* Server info */}
          {serverInfo && (serverInfo.name || serverInfo.description) && (
            <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
              {serverInfo.name && (
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {serverInfo.name}
                </h3>
              )}
              {serverInfo.author && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('resources.assistants.integrations.mcp.by_author', { author: serverInfo.author })}
                </p>
              )}
              {serverInfo.description && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {serverInfo.description}
                </p>
              )}
              {serverInfo.tags && serverInfo.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {serverInfo.tags.map((tag: string, i: number) => (
                    <span
                      key={`${tag}-${i}`}
                      className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="max-w-lg space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('resources.assistants.integrations.mcp.alias')}</label>
              <Input
                value={alias}
                onChange={setAlias}
                placeholder={t('resources.assistants.integrations.mcp.alias_placeholder')}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('resources.assistants.integrations.mcp.url_required')}</label>
              <Input
                value={url}
                onChange={setUrl}
                placeholder="https://example.com/mcp"
                className={url && !isValidHttpUrl(url) ? 'border-red-500' : ''}
              />
              {url && !isValidHttpUrl(url) && (
                <p className="text-xs text-red-500">{t('resources.assistants.integrations.mcp.url_invalid')}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('resources.assistants.integrations.mcp.type')}</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as MCPTransportType)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="streamable_http">streamable_http</option>
                <option value="sse">sse</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('resources.assistants.integrations.mcp.auth_token')}
              </label>
              <Input
                type="password"
                value={authToken}
                onChange={setAuthToken}
                placeholder={t('resources.assistants.integrations.mcp.auth_token_placeholder')}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('resources.assistants.integrations.mcp.headers')}</label>
              {headers.map((row, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    placeholder={t('resources.assistants.integrations.mcp.header_key')}
                    value={row.key}
                    onChange={(val) =>
                      setHeaders((prev) =>
                        prev.map((r, i) => (i === idx ? { ...r, key: val } : r))
                      )
                    }
                    className="flex-1"
                  />
                  <Input
                    placeholder={t('resources.assistants.integrations.mcp.header_value')}
                    value={row.value}
                    onChange={(val) =>
                      setHeaders((prev) =>
                        prev.map((r, i) => (i === idx ? { ...r, value: val } : r))
                      )
                    }
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    color="tertiary"
                    size="sm"
                    onPress={() => setHeaders((prev) => prev.filter((_, i) => i !== idx))}
                    className="text-red-500"
                    iconLeading={X}
                  />
                </div>
              ))}
              <Button
                type="button"
                color="secondary"
                size="sm"
                onClick={() => setHeaders((prev) => [...prev, { key: '', value: '' }])}
              >
                {t('resources.assistants.integrations.mcp.add_header')}
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button type="button" onPress={handleSave} isDisabled={!isValid || isSaving} isLoading={isSaving}>
            {t('resources.assistants.integrations.save_changes')}
          </Button>
        </div>
      </div>
    </div>
  )
}
